/**
 * DeliverySettingsCard Component
 *
 * Email delivery settings card with:
 * - Timezone configuration
 * - Quiet hours settings
 *
 * Design Compliance:
 * - Single Card pattern (matches other settings cards)
 * - Icon + Title header
 * - Uses Polaris spacing scale (gap="400", gap="500")
 */

import { useMemo } from 'react';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Box,
  Icon,
  Select,
} from '@shopify/polaris';
import { ClockIcon } from '@shopify/polaris-icons';
import { TIMEZONE_OPTIONS } from '../../../constants/timezones';
import { Toggle } from '../../ui';

export interface DeliverySettingsCardProps {
  /** Current timezone setting */
  timezone: string;
  /** Handler for timezone change */
  onTimezoneChange: (timezone: string) => void;
  /** Whether quiet hours are enabled */
  quietHoursEnabled: boolean;
  /** Quiet hours start time */
  quietHoursStart: string;
  /** Quiet hours end time */
  quietHoursEnd: string;
  /** Handler for quiet hours enabled change */
  onQuietHoursEnabledChange: (enabled: boolean) => void;
  /** Handler for quiet hours start time change */
  onQuietHoursStartChange: (time: string) => void;
  /** Handler for quiet hours end time change */
  onQuietHoursEndChange: (time: string) => void;
}

export function DeliverySettingsCard({
  timezone,
  onTimezoneChange,
  quietHoursEnabled,
  quietHoursStart,
  quietHoursEnd,
  onQuietHoursEnabledChange,
  onQuietHoursStartChange,
  onQuietHoursEndChange,
}: DeliverySettingsCardProps) {
  // Time options for selects (30-min intervals)
  const timeOptions = useMemo(() => {
    const options: Array<{ label: string; value: string }> = [];
    for (let hour = 0; hour < 24; hour++) {
      for (const minute of [0, 30]) {
        const hourStr = hour.toString().padStart(2, '0');
        const minStr = minute.toString().padStart(2, '0');
        const value = `${hourStr}:${minStr}`;
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour < 12 ? 'AM' : 'PM';
        const label = `${displayHour}:${minStr.padStart(2, '0')} ${ampm}`;
        options.push({ label, value });
      }
    }
    return options;
  }, []);

  return (
    <Card>
      <BlockStack gap="400">
        {/* Header */}
        <InlineStack gap="200" blockAlign="start" wrap={false}>
          <Box paddingBlockStart="050">
            <Icon source={ClockIcon} tone="subdued" />
          </Box>
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">
              Delivery settings
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Configure when notifications are delivered
            </Text>
          </BlockStack>
        </InlineStack>

        {/* Content */}
        <BlockStack gap="400">
          <Select
            label="Timezone"
            options={TIMEZONE_OPTIONS}
            value={timezone}
            onChange={onTimezoneChange}
          />

          <InlineStack align="space-between" blockAlign="start" wrap={false}>
            <BlockStack gap="100">
              <Text as="p" variant="bodyMd">
                Enable quiet hours
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {quietHoursEnabled
                  ? `Notifications paused ${quietHoursStart} to ${quietHoursEnd}`
                  : 'Pause non-critical notifications during specified hours'}
              </Text>
            </BlockStack>
            <Toggle
              label="Enable quiet hours"
              enabled={quietHoursEnabled}
              onChange={onQuietHoursEnabledChange}
            />
          </InlineStack>

          {quietHoursEnabled && (
            <Box>
              <InlineStack gap="200" blockAlign="center" wrap={false}>
                <Box minWidth="120px">
                  <Select
                    label="Start time"
                    labelHidden
                    options={timeOptions}
                    value={quietHoursStart}
                    onChange={onQuietHoursStartChange}
                  />
                </Box>
                <Text as="span" variant="bodySm" tone="subdued">
                  to
                </Text>
                <Box minWidth="120px">
                  <Select
                    label="End time"
                    labelHidden
                    options={timeOptions}
                    value={quietHoursEnd}
                    onChange={onQuietHoursEndChange}
                  />
                </Box>
              </InlineStack>
            </Box>
          )}
        </BlockStack>
      </BlockStack>
    </Card>
  );
}
